/**
 * usePassengerVoices — TanStack Query hooks for the passenger-silence counter
 * and single-influencer spotlight.
 *
 * Two procedures are exposed:
 *   - passengerVoices.summary → PassengerVoiceSummaryOut | null
 *   - passengerVoices.topInfluencer → TopInfluencerOut | null
 *
 * Dates arrive as ISO strings from tRPC and are normalised to Date objects.
 * Both hooks use staleTime: 60_000 (1 min) so the counter re-fetches live
 * without hammering the API.
 *
 * Prompt #11 (passenger-silence-and-influencer-feed).
 */

import { useQuery } from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// Types (mirrors the tRPC output types)
// ---------------------------------------------------------------------------

export type PassengerVoiceSummaryOut = {
  outbreakId: number;
  slug: string;
  daysSinceDetected: number;
  passengerHeadcount: number | null;
  distinctVerifiedHandles: number;
  lastVerifiedAt: Date | null;
};

export type TopInfluencerOut = {
  voiceId: string;
  handle: string;
  platform: "instagram" | "tiktok" | "x" | "facebook" | "threads";
  followersCount: number | null;
  postUrl: string;
  contentExcerpt: string;
  capturedAt: Date;
  sourceUrl: string;
  verifiedAt: Date;
} | null;

type RawSummary = Omit<
  PassengerVoiceSummaryOut,
  "lastVerifiedAt"
> & { lastVerifiedAt: string | null };

type RawInfluencer = {
  voiceId: string;
  handle: string;
  platform: "instagram" | "tiktok" | "x" | "facebook" | "threads";
  followersCount: number | null;
  postUrl: string;
  contentExcerpt: string;
  capturedAt: string;
  sourceUrl: string;
  verifiedAt: string;
} | null;

// ---------------------------------------------------------------------------
// Normalise helpers
// ---------------------------------------------------------------------------

function normaliseSummary(raw: RawSummary): PassengerVoiceSummaryOut {
  return {
    ...raw,
    lastVerifiedAt: raw.lastVerifiedAt ? new Date(raw.lastVerifiedAt) : null,
  };
}

function normaliseInfluencer(raw: RawInfluencer): TopInfluencerOut {
  if (!raw) return null;
  return {
    ...raw,
    capturedAt: new Date(raw.capturedAt),
    verifiedAt: new Date(raw.verifiedAt),
  };
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

function apiBase(): string {
  return (
    (
      typeof import.meta !== "undefined"
        ? (import.meta.env?.VITE_API_URL as string | undefined)
        : undefined
    ) ?? ""
  ).replace(/\/+$/u, "");
}

async function fetchSummary(
  slug: string,
): Promise<PassengerVoiceSummaryOut | null> {
  const encoded = encodeURIComponent(JSON.stringify({ json: { slug } }));
  const url = `${apiBase()}/api/trpc/passengerVoices.summary?input=${encoded}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) throw new Error(`passengerVoices.summary failed: ${res.status}`);

  const json = (await res.json()) as {
    result?: { data?: { json?: RawSummary | null } | (RawSummary | null) };
  };

  const inner = json.result?.data;
  const data =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: RawSummary | null }).json
      : (inner as RawSummary | null | undefined);

  if (data === undefined) throw new Error("passengerVoices.summary: malformed response");
  if (!data) return null;
  return normaliseSummary(data);
}

async function fetchTopInfluencer(slug: string): Promise<TopInfluencerOut> {
  const encoded = encodeURIComponent(JSON.stringify({ json: { slug } }));
  const url = `${apiBase()}/api/trpc/passengerVoices.topInfluencer?input=${encoded}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok)
    throw new Error(`passengerVoices.topInfluencer failed: ${res.status}`);

  const json = (await res.json()) as {
    result?: {
      data?: { json?: RawInfluencer } | RawInfluencer;
    };
  };

  const inner = json.result?.data;
  const raw =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: RawInfluencer }).json
      : (inner as RawInfluencer | undefined);

  return normaliseInfluencer(raw ?? null);
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

export function usePassengerVoicesSummary(opts: { slug: string }) {
  return useQuery<PassengerVoiceSummaryOut | null>({
    queryKey: ["passengerVoices", "summary", opts.slug],
    queryFn: () => fetchSummary(opts.slug),
    staleTime: 60_000,
    retry: 2,
  });
}

export function useTopInfluencer(opts: { slug: string }) {
  return useQuery<TopInfluencerOut>({
    queryKey: ["passengerVoices", "topInfluencer", opts.slug],
    queryFn: () => fetchTopInfluencer(opts.slug),
    staleTime: 60_000,
    retry: 2,
  });
}
